import { useState } from 'react';

import { Link } from '@tanstack/react-router';

import { Button, Icon, IconButton } from '@/components/ui';
import { getConfig } from '@/lib/config';

export function LinkedInExtensionPage() {
  const apiBaseUrl = useApiBaseUrl();
  const storeUrl = getConfig().linkedInExtensionUrl;
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <header className="page-header">
        <div>
          <h1 className="page-title">LinkedIn Chrome extension</h1>
          <div className="page-subtitle">
            Save LinkedIn profiles into Datahub with one click. The extension
            uploads the profile PDF and Datahub auto-creates or merges the
            contact by email.
          </div>
        </div>
      </header>

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          maxWidth: 760,
        }}
      >
        <Step
          n={1}
          title="Install the extension"
          body={
            <>
              Download the latest zip from the Werk Store and unzip it into a
              permanent folder. In <code>chrome://extensions</code> turn on{' '}
              <code>Developer mode</code>, click <code>Load unpacked</code> and
              pick the unzipped folder. To update, replace the folder contents and
              click <code>Reload</code> on the extension.
            </>
          }
          action={
            storeUrl ? (
              <a href={storeUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Icon name="download" size={12} />}
                >
                  Open in Werk Store
                </Button>
              </a>
            ) : undefined
          }
        />

        <Step
          n={2}
          title="Create a personal access token"
          body={
            <>
              Tokens are scoped — for the extension, pick the{' '}
              <code>imports:linkedin</code> scope. The token is shown exactly
              once at creation.
            </>
          }
          action={
            <Link to="/settings/api-tokens">
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Icon name="plus" size={12} />}
              >
                Open API tokens
              </Button>
            </Link>
          }
        />

        <Step
          n={3}
          title="Configure the extension"
          body={
            <>
              In the extension&apos;s popup, paste the API base URL and the token you
              just created. Both fields are stored in the extension&apos;s local
              storage and never leave the browser.
            </>
          }
        >
          <CopyRow
            label="API base URL"
            value={apiBaseUrl}
            copied={copied === 'API base URL'}
            onCopy={() => void copy('API base URL', apiBaseUrl)}
          />
        </Step>

        <Step
          n={4}
          title="Use it"
          body={
            <>
              Open any LinkedIn profile. The extension&apos;s button uploads the
              profile PDF; Datahub extracts the email, finds the matching
              contact (or creates a new one), and dumps the parsed details into
              a comment under the profile.
            </>
          }
        />
      </div>
    </div>
  );
}

function Step({
  n,
  title,
  body,
  action,
  children,
}: {
  n: number;
  title: string;
  body: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: 'var(--brand)',
          color: 'white',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--font-ui)',
          fontWeight: 600,
          fontSize: 13,
          flexShrink: 0,
        }}
      >
        {n}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontWeight: 600,
            fontSize: 14,
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>{body}</div>
        {(action || children) && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {action}
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

function CopyRow({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: '8px 10px',
      }}
    >
      <span style={{ fontSize: 11, color: 'var(--text-3)', minWidth: 90 }}>{label}</span>
      <code
        style={{
          flex: 1,
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          color: 'var(--text)',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </code>
      <IconButton
        icon={copied ? 'check' : 'link'}
        iconSize={12}
        size="sm"
        label={copied ? 'Copied' : `Copy ${label}`}
        onClick={onCopy}
      />
    </div>
  );
}

function useApiBaseUrl() {
  // Resolved once on mount via a lazy initializer (no setState-in-effect).
  const [url] = useState(() => {
    // Best-effort: pull from the runtime config the app already loads.
    const fromWindow = (window as unknown as { __DATAHUB_API_URL__?: string }).__DATAHUB_API_URL__;
    if (fromWindow) return fromWindow.replace(/\/+$/, '');
    // Fallback to same-origin API path.
    return `${window.location.origin}/api/v1`;
  });
  return url;
}
